import type { Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { fromSegment, type HeadPicture, mask, type SetFlair } from './mythic';

/**
 * The Origami set (ADR-0160 amended, mythic): a warrior folded out of paper. A paper samurai helmet (the folded kabuto)
 * with a paper crane perched on its peak, flapping, over a white mask of folded facets; folded-paper armour; the Foldfire.
 *
 * The head is a mask over the whole head: the kabuto in the team's paper (two facets either side of its crease, lit on
 * the left, its two horn flaps folded up and out), a cream brim folded up across the brow, under it a white mask of flat
 * facets (lit top-left, a crease down the nose's ridge and one across the cheeks) with slit eyes in the team's light and
 * a red seal on the cheek. On the helmet's peak sits a paper crane: a diamond body, a neck rising to a pointed head
 * with a red crown, a tail rising behind. Its wings are the head's flair (a `glyph`): a white near wing and a far one in
 * the team's paper beating up and down, the bright moving part that reads at 1:1. The suit is pleated paper armour: fan-
 * folded shoulder plates in the team's paper, a white faceted breastplate, a red obi, a cream fan-folded skirt under it.
 * Its flair: two streamers of the team's paper swinging from the obi's knot, paper scraps drifting off it.
 *
 * Rubric (`assetc mythic origami` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside Disco,
 * Glitch and Inferno): head S4 R5 P4 L4 C4 A4 F4, armour S4 R4 P4 L4 C4 F4. Still weak: the crane is 26 px long, a
 * white tick with beating wings at 1:1 (its wings carry it); the armour shows little past the gun (the shoulder's pleats
 * and the streamers carry it).
 */

// --- The kabuto, the mask and the crane ------------------------------------------------------------------------------

type Pt = readonly [number, number];

/** Whether (u, v) is inside the polygon `pts` (even-odd). */
function inPoly(u: number, v: number, pts: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i] as Pt;
    const [xj, yj] = pts[j] as Pt;
    if (yi > v !== yj > v && u < ((xj - xi) * (v - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The helmet: a triangle of the team's paper, its crease from the peak down. */
const HELM: readonly Pt[] = [
  [-21, -5],
  [1, -18],
  [22, -5],
];
const CREASE = [1, -18, 4, -5] as const;
/** Its horn flaps, folded up and out either side. */
const HORNS: readonly (readonly Pt[])[] = [
  [
    [-10, -9],
    [-26, -22],
    [-18, -5],
  ],
  [
    [12, -9],
    [27, -21],
    [19, -5],
  ],
];
/** The mask standing (a folded jaw to a chin point) and lying (the dead face is bigger: it reaches further). */
const FACE: readonly Pt[] = [
  [-21, -4],
  [20, -4],
  [20, 8],
  [15, 15],
  [4, 18.5],
  [-15, 15],
  [-21, 7],
];
const FACE_LYING: readonly Pt[] = [
  [-19, -4],
  [20, -4],
  [20, 18],
  [12, 23],
  [-12, 23],
  [-19, 16],
];
/** The nose's flap, folded out from the ridge. */
const NOSE = [
  [4, 1],
  [10, 8.5],
  [4, 9],
] as const satisfies readonly Pt[];
/** The mask's creases: down the nose's ridge, across the cheeks. */
const RIDGE = [4, -4, 4, 23] as const;
const CHEEK = [-21, 8, 4, 10, 20, 6] as const;
/** The eyes: slits slanting down to the nose, a glint of the team's light at the inner end. */
const EYES = [
  [-9, 0.8, -1, 2.8],
  [8, 2.6, 13.5, 0.6],
] as const;

/** The brim folded up across the brow: its top edge at u, curving down to its points. */
const brimTop = (u: number) => -7.5 + (u / 22) ** 2 * 2;

/** The crane on the peak: its body a diamond, its neck and its tail rising. */
const BODY = { u: 1.5, v: -19.5, a: 8, b: 3.4 } as const;
const NECK = [6, -20.5, 11.5, -27] as const;
const BEAK = [11.5, -27, 16.5, -26] as const;
const TAIL = [-4, -20.5, -11.5, -27.3] as const;

/** The crane: white paper lit on its top, a crease down its body, a red crown; null off it. */
function crane(u: number, v: number): PaletteKey | null {
  if (Math.hypot(u - 11, v + 27.3) < 1) return 'r';
  if (fromSegment(u, v, ...BEAK) < 0.7) return '5';
  if (fromSegment(u, v, ...NECK) < 1.5)
    return (NECK[2] - NECK[0]) * (v - NECK[1]) - (NECK[3] - NECK[1]) * (u - NECK[0]) < 0 ? 'w' : '5';
  // The tail: narrowing to its point.
  const t = Math.max(0, Math.min(1, (u - TAIL[0]) / (TAIL[2] - TAIL[0])));
  if (fromSegment(u, v, ...TAIL) < 1.6 - t * 1.1) return v < TAIL[1] + (TAIL[3] - TAIL[1]) * t ? 'w' : '5';
  const d = Math.abs(u - BODY.u) / BODY.a + Math.abs(v - BODY.v) / BODY.b;
  if (d > 1) return null;
  if (Math.abs(v - BODY.v) < 0.5) return '4';
  return v < BODY.v ? 'w' : '5';
}

const kabuto: HeadPicture = (u, v, _lx, _ly, lying) => {
  const bird = crane(u, v);
  if (bird) return bird;
  // The brim, cream: its lit top, its shaded foot, the folds at its points.
  const top = brimTop(u);
  if (Math.abs(u) < 23 - (v - top) * 0.8 && v >= top && v < top + 4) {
    if (v < top + 1) return 'e';
    if (v >= top + 3) return 't';
    return Math.abs(Math.abs(u) - 13) < 0.5 ? 't' : 'e';
  }
  const face = lying ? FACE_LYING : FACE;
  if (inPoly(u, v, face)) {
    // The seal on the cheek.
    if (Math.abs(u + 13) < 1.6 && Math.abs(v - 11) < 1.6)
      return Math.abs(u + 13) < 0.6 && Math.abs(v - 11) < 0.6 ? 'i' : 'r';
    for (const [x0, y0, x1, y1] of EYES) {
      if (fromSegment(u, v, x0, y0, x1, y1) < 1.05) {
        // The glint by the nose.
        const inner = x0 < 0 ? x1 - 1.6 : x0 + 1.4;
        return Math.abs(u - inner) < 1 ? 'c' : 'k';
      }
    }
    // The nose: a flap folded out from the ridge, lit on its top, its fold shaded.
    if (!lying && inPoly(u, v, NOSE))
      return fromSegment(u, v, NOSE[1][0], NOSE[1][1], NOSE[2][0], NOSE[2][1]) < 0.8 ? '4' : 'w';
    const right = u > RIDGE[0];
    const cheekY = u < CHEEK[2] ? CHEEK[1] + ((u - CHEEK[0]) / (CHEEK[2] - CHEEK[0])) * 2 : 10 - ((u - 4) / 16) * 4;
    const low = v > cheekY;
    if (fromSegment(u, v, ...RIDGE) < 0.5) return low ? '3' : '4';
    if (Math.abs(v - cheekY) < 0.5) return right ? '3' : '4';
    // The far side of the jaw, turned away.
    if (right && low && inPoly(u, v, face) && v > 14 - (u - 4) * 0.2) return '3';
    return right ? (low ? '4' : '5') : low ? '5' : 'w';
  }
  if (inPoly(u, v, HELM) || (lying && Math.abs(u - 1) < 9 && v > -24 && v < -4)) {
    if (fromSegment(u, v, ...CREASE) < 0.55) return 'c';
    const left = u < CREASE[0] + ((v - CREASE[1]) / (CREASE[3] - CREASE[1])) * (CREASE[2] - CREASE[0]);
    // The lit rim down its left slope, a fold across its foot.
    if (left && fromSegment(u, v, -21, -5, 1, -18) < 1.3) return 'c';
    if (v > -8.5 && v < -7.5) return left ? 'B' : 'B';
    return left ? 'b' : 'B';
  }
  for (const [i, horn] of HORNS.entries()) {
    if (!inPoly(u, v, horn)) continue;
    const [a, b] = [horn[0] as Pt, horn[1] as Pt];
    // Lit along its upper edge (the left horn's top, the right horn's inner side), dark below.
    if (fromSegment(u, v, a[0], a[1], b[0], b[1]) < 1.2) return i === 0 ? 'c' : 'b';
    return i === 0 ? 'b' : 'B';
  }
  return null;
};

// --- The paper armour ------------------------------------------------------------------------------------------------

/**
 * The armour standing (the frame's x 5-56, y 39-57): a white breastplate of folded facets, shoulder plates fan-folded in
 * the team's paper, a red obi with a white fold, a cream skirt of pleats under it.
 */
function armour(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  // The breastplate: facets either side of a crease down its middle, a fold across it.
  for (let y = 43; y <= 52; y++)
    for (let x = 16; x <= 43; x++) {
      const crease = x === 30 || (y === 48 && x !== 30);
      const near = x < 30;
      at(x, y, crease ? (near ? '5' : '3') : near ? (y < 48 ? 'w' : '5') : y < 48 ? '5' : '4');
    }
  // A V of the team's paper at the neck.
  for (let y = 42; y <= 46; y++) {
    const half = 46 - y;
    for (let x = 30 - half; x <= 30 + half; x++) at(x, y, x < 30 ? 'b' : 'B');
  }
  // The shoulder plates: fans of pleats from the neck, the team's paper lit and shaded in turn.
  for (const [cx, rx] of [
    [12.5, 7.5],
    [47, 6.5],
  ] as const) {
    for (let y = 41; y <= 54; y++)
      for (let x = 5; x <= 56; x++) {
        const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
        if (dx * dx + dy * dy > 1 || dy < -1) continue;
        // Pleats radiating from the plate's top middle.
        const a = Math.atan2(y + 0.5 - 41, x + 0.5 - cx);
        const pleat = Math.floor(a / 0.3) % 2 === 0;
        const rim = dx * dx + dy * dy > 0.72 && dy > 0;
        at(x, y, rim ? 'B' : pleat ? (dx < 0.2 ? 'c' : 'b') : dx < -0.3 ? 'b' : 'B');
      }
  }
  // The red obi, a white fold of paper tucked in it.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, x <= 18 ? 'i' : 'r');
    at(x, 54, 'r');
    at(x, 55, 'R');
  }
  c.stamp(
    `
    .w5.
    w5w4
    .54.
  `,
    22 - x0,
    53 - y0,
  );
  // The skirt's pleats under it.
  for (let x = 17; x <= 42; x++) {
    const pleat = Math.floor((x - 17) / 2) % 2 === 0;
    at(x, 56, pleat ? 'e' : 't');
    at(x, 57, pleat ? 't' : 'm');
  }
  return [c, x0, y0];
}

/** The armour lying (right of the legs, under the head): the obi at the waist (left), the plate's facets, the pleats. */
const DEAD_ARMOUR = grid(`
  ....bbcc
  ...bcbcB
  rReb5wwB
  rRtbw5wB
  rRew5ww5
  rRtw5ww5
  rRe5bbw5
  rRt5bB55
  rRew5ww5
  rRtw5ww5
  rRe5www5
  rRtw5w55
  rRew5ww5
  rRtbbbcB
  rRebcbbc
  rRtbbbbc
  rRBBBBBB
  ....BBBB
`);

/** Cream paper sandals under the team's trousers. */
const ARMOUR = (): Outfit => ({ torso: armour(), dead: [DEAD_ARMOUR, 24, 51], legs: { n: 't', N: 'm', m: 'e' } });

// --- The Foldfire's finish on every other gun ------------------------------------------------------------------------

/** The paper finish (the default gun is its own, `pollinator_origami`): white paper for steel, cream for wood, red for brass. */
const FOLDED = {
  '3': '4',
  '4': '5',
  '5': 'w',
  N: 'm',
  n: 't',
  m: 'e',
  t: 'e',
  U: 'R',
  Y: 'r',
  y: 'i',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const PAPER = 0xc0cbdc;
const CREAM = 0xead4aa;
const SLATE = 0x5a6988;
const RED = 0xe43b44;

/** The wings' root in their glyph, and the box they beat in. */
const ROOT = [14, 18] as const;
const WING_BOX = [30, 32] as const;

/**
 * A frame of the wings: the near wing (white, a crease down it) and the far one (the team's paper) as triangles from
 * their root on the crane's back to tips at `near` and `far` (the glyph's px), each rimmed in slate.
 */
function wings(near: Pt, far: Pt): string {
  const rows = Array.from({ length: WING_BOX[1] }, () => Array(WING_BOX[0]).fill('.') as string[]);
  const tri = (a: Pt, b: Pt, tip: Pt, fill: string, crease: string, rim: boolean) => {
    for (let y = 0; y < WING_BOX[1]; y++)
      for (let x = 0; x < WING_BOX[0]; x++) {
        const [px, py] = [x + 0.5, y + 0.5];
        if (!inPoly(px, py, [a, b, tip])) continue;
        const row = rows[y] as string[];
        const edge =
          !inPoly(px - 1, py, [a, b, tip]) ||
          !inPoly(px + 1, py, [a, b, tip]) ||
          !inPoly(px, py - 1, [a, b, tip]) ||
          !inPoly(px, py + 1, [a, b, tip]);
        const mid: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        row[x] = edge && rim ? 'o' : fromSegment(px, py, mid[0], mid[1], tip[0], tip[1]) < 0.6 ? crease : fill;
      }
  };
  tri([ROOT[0] - 3, ROOT[1]], [ROOT[0] + 6, ROOT[1]], far, 't', 'd', false);
  tri([ROOT[0] - 8, ROOT[1] + 1], [ROOT[0] + 5, ROOT[1] + 1], near, 'w', 's', true);
  return rows.map((r) => r.join('')).join('\n');
}

/** A beat: up, half up, level, down, level, half up; the tips sweep back as they rise. */
const BEAT = [
  wings([6, 0], [15, 0]),
  wings([1, 7], [12, 3]),
  wings([0, 15], [28, 13]),
  wings([5, 31], [23, 27]),
  wings([0, 17], [28, 14]),
  wings([1, 8], [12, 4]),
];

const flapping: Glyph = {
  kind: 'glyph',
  // The crane's back (the frame's (32, 6)) at the wings' root.
  at: { on: 'head', x: 31 - ROOT[0], y: 8 - ROOT[1] },
  frames: BEAT,
  tones: { w: WHITE, s: PAPER, t: 'team', d: 'teamDark', o: SLATE },
  hold: [0.075, 0.075],
  order: 'cycle',
  layer: 'front',
};

/** Two streamers of the team's paper from the obi's knot at its back. */
const streamer = (dir: number, sway: number, phase: number, n: number) =>
  ({
    kind: 'chain',
    at: { on: 'torso', x: 15, y: 54 },
    n,
    seg: 2.6,
    dir,
    stiff: 2.4,
    g: 180,
    drag: 0.05,
    sway: [0.22, sway, phase],
    wind: 260,
    w: [3, 3, 2],
    shape: 'bars',
    tones: ['team', 'team', 'team', 'teamLight'],
    rim: 'teamDark',
    lit: 'teamLight',
    tatter: 2,
    layer: 'back',
  }) as const;

const FLAIR: SetFlair = {
  head: [
    flapping,
    // A scrap of paper now and then, shaken off the beating wings.
    {
      kind: 'emit',
      at: { on: 'head', x: 32, y: 2 },
      box: [16, 6],
      rate: 0.9,
      v: [-10, -3, -6, 2],
      g: 30,
      drag: 0.5,
      wobble: [3, 1.2],
      life: [0.8, 1.3],
      size: [2, 2],
      tones: [WHITE, CREAM],
      alpha: [1, 0.4],
      layer: 'front',
    },
  ],
  outfit: [
    streamer(Math.PI * 0.62, 0.9, 0, 6),
    streamer(Math.PI * 0.74, 1.1, 0.4, 5),
    // Paper scraps drifting off the armour, more of them on the run.
    {
      kind: 'emit',
      at: { on: 'torso', x: 26, y: 50 },
      box: [34, 14],
      rate: 1.4,
      run: 0.03,
      v: [-12, -4, -4, 6],
      carry: 0.3,
      g: 22,
      drag: 0.5,
      wobble: [3, 1],
      life: [0.9, 1.5],
      size: [2, 2],
      tones: [WHITE, CREAM, 'team', RED],
      alpha: [1, 0.5],
      layer: 'front',
    },
  ],
  gun: [
    // The fan on its back breathing a soft white, flecks of paper puffed from its muzzle.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 34, y: 7 },
      r: [3, 6],
      alpha: [0.15, 0.35],
      period: 0.9,
      tone: WHITE,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 62, y: 20 },
      box: [2, 4],
      rate: 1.6,
      v: [4, 12, -8, -2],
      g: 20,
      drag: 0.5,
      wobble: [2, 1.5],
      life: [0.5, 0.9],
      size: [1, 1],
      tones: [WHITE, CREAM],
      layer: 'gun',
    },
  ],
};

/** The Origami set (ADR-0160 amended). */
export const ORIGAMI = drawnLater({ head: () => mask(kabuto), outfit: ARMOUR, gun: FOLDED, flair: FLAIR });
